import { useEffect, useState, useMemo } from 'react';
import { useStudent } from '../context/StudentContext';
import { preferencesApi, type School, highSchoolsApi, type HighSchool } from '../api';
import { Plus, Trash2, GripVertical, Save, Download, Upload, X, ChevronUp, ChevronDown, AlertTriangle, CheckCircle, Star } from 'lucide-react';
import { schoolsApi } from '../api';

interface Volunteer {
  id: number;
  school_name: string;
  major_name?: string;
  category: string;
  priority: number;
  notes?: string;
}

export default function VolunteerSim() {
  const { currentStudent } = useStudent();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRiskPanel, setShowRiskPanel] = useState(true);
  const [searchSchool, setSearchSchool] = useState('');
  const [schoolResults, setSchoolResults] = useState<School[]>([]);
  const [highSchoolResults, setHighSchoolResults] = useState<HighSchool[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [selectedHighSchool, setSelectedHighSchool] = useState<HighSchool | null>(null);
  const [majorName, setMajorName] = useState('');
  const [category, setCategory] = useState('稳妥');
  const [saving, setSaving] = useState(false);

  const educationLevel = currentStudent?.education_level || 'senior';
  const isJunior = educationLevel === 'junior';

  useEffect(() => {
    if (!currentStudent) return;
    loadVolunteers();
  }, [currentStudent?.id]);

  const loadVolunteers = async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const res = await preferencesApi.getVolunteer(currentStudent.id);
      setVolunteers(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const searchSchools = async (query: string) => {
    if (!query.trim()) {
      setSchoolResults([]);
      setHighSchoolResults([]);
      return;
    }
    try {
      if (isJunior) {
        // 初中生：搜索高中
        const res = await highSchoolsApi.list({ search: query });
        setHighSchoolResults(res.data.data?.slice(0, 10) || []);
        setSchoolResults([]);
      } else {
        // 高中生：搜索大学
        const res = await schoolsApi.list({ search: query });
        setSchoolResults(res.data.data?.slice(0, 10) || []);
        setHighSchoolResults([]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAdd = async () => {
    if (!currentStudent) return;
    setSaving(true);
    try {
      const schoolName = isJunior ? selectedHighSchool?.name : selectedSchool?.name;
      if (!schoolName) {
        setSaving(false);
        return;
      }
      await preferencesApi.addVolunteer({
        student_id: currentStudent.id,
        school_name: schoolName,
        major_name: majorName || undefined,
        category,
      });
      setShowAddDialog(false);
      setSelectedSchool(null);
      setSelectedHighSchool(null);
      setMajorName('');
      setCategory('稳妥');
      setSearchSchool('');
      setSchoolResults([]);
      setHighSchoolResults([]);
      loadVolunteers();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个志愿吗？')) return;
    try {
      await preferencesApi.deleteVolunteer(id);
      loadVolunteers();
    } catch (e) {
      console.error(e);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newList = [...volunteers];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setVolunteers(newList.map((v, i) => ({ ...v, priority: i + 1 })));
  };

  const moveDown = (index: number) => {
    if (index === volunteers.length - 1) return;
    const newList = [...volunteers];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setVolunteers(newList.map((v, i) => ({ ...v, priority: i + 1 })));
  };

  const saveOrder = async () => {
    if (!currentStudent) return;
    setSaving(true);
    try {
      await Promise.all(
        volunteers.map((v, i) => 
          preferencesApi.updateVolunteer(v.id, { priority: i + 1 })
        )
      );
      alert('排序已保存');
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // 风险分析
  const riskAnalysis = useMemo(() => {
    const categories = {
      '冲刺': volunteers.filter(v => v.category === '冲刺').length,
      '稳妥': volunteers.filter(v => v.category === '稳妥').length,
      '保底': volunteers.filter(v => v.category === '保底').length,
    };
    const total = volunteers.length;
    
    let risk = 'low';
    let message = '';
    
    if (total === 0) {
      risk = 'none';
      message = '请添加志愿';
    } else if (categories['保底'] === 0) {
      risk = 'high';
      message = '缺少保底院校，风险较高';
    } else if (categories['冲刺'] > categories['保底'] * 2) {
      risk = 'medium';
      message = '冲刺院校过多，建议增加保底';
    } else if (total < 10) {
      risk = 'medium';
      message = '志愿数量不足，建议填报更多志愿';
    } else if (categories['稳妥'] < total * 0.4) {
      risk = 'medium';
      message = '稳妥院校偏少，建议增加';
    } else {
      message = '志愿结构合理';
    }
    
    return { categories, total, risk, message };
  }, [volunteers]);

  const categoryColors: Record<string, { bg: string; border: string; badge: string; text: string }> = {
    '冲刺': { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700' },
    '稳妥': { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-500', text: 'text-green-700' },
    '保底': { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-500', text: 'text-blue-700' },
  };

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">志愿模拟器</h1>
          <p className="text-gray-500 text-sm mt-1">
            创建和管理志愿表，评估录取风险
          </p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          添加志愿
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* 志愿列表 */}
        <div className="flex-1">
          {/* 操作栏 */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-500">
              已添加 <span className="font-medium text-gray-700">{volunteers.length}</span> 个志愿
              <span className="ml-3 text-gray-400">（最多96个）</span>
            </div>
            <button
              onClick={saveOrder}
              disabled={saving || volunteers.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              保存排序
            </button>
          </div>

          {/* 志愿卡片 */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : volunteers.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center text-gray-400">
              <Star size={48} className="opacity-30 mx-auto mb-4" />
              <p>暂无志愿</p>
              <p className="text-sm mt-2">点击上方按钮添加志愿</p>
            </div>
          ) : (
            <div className="space-y-2">
              {volunteers.map((volunteer, index) => {
                const colors = categoryColors[volunteer.category] || categoryColors['稳妥'];
                return (
                  <div
                    key={volunteer.id}
                    className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      {/* 排序按钮 */}
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveUp(index)}
                          disabled={index === 0}
                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          onClick={() => moveDown(index)}
                          disabled={index === volunteers.length - 1}
                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      
                      {/* 序号 */}
                      <div className={`w-8 h-8 ${colors.badge} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
                        {index + 1}
                      </div>
                      
                      {/* 院校信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800 truncate">{volunteer.school_name}</h4>
                          {volunteer.major_name && (
                            <span className="text-sm text-gray-500">- {volunteer.major_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${colors.bg} ${colors.text}`}>
                            {volunteer.category}
                          </span>
                          {volunteer.notes && (
                            <span className="text-xs text-gray-400">{volunteer.notes}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* 删除按钮 */}
                      <button
                        onClick={() => handleDelete(volunteer.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 风险分析侧边栏 */}
        <div className="w-full lg:w-72 flex-shrink-0">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">风险评估</h3>
              <button
                onClick={() => setShowRiskPanel(!showRiskPanel)}
                className="text-gray-400 hover:text-gray-600"
              >
                {showRiskPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {showRiskPanel && (
              <>
                {/* 风险状态 */}
                <div className={`p-4 rounded-xl mb-4 ${
                  riskAnalysis.risk === 'low' ? 'bg-green-50' :
                  riskAnalysis.risk === 'medium' ? 'bg-amber-50' :
                  riskAnalysis.risk === 'high' ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {riskAnalysis.risk === 'low' && <CheckCircle size={18} className="text-green-600" />}
                    {riskAnalysis.risk === 'medium' && <AlertTriangle size={18} className="text-amber-600" />}
                    {riskAnalysis.risk === 'high' && <AlertTriangle size={18} className="text-red-600" />}
                    <span className={`font-medium ${
                      riskAnalysis.risk === 'low' ? 'text-green-700' :
                      riskAnalysis.risk === 'medium' ? 'text-amber-700' :
                      riskAnalysis.risk === 'high' ? 'text-red-700' : 'text-gray-600'
                    }`}>
                      {riskAnalysis.risk === 'low' ? '风险较低' :
                       riskAnalysis.risk === 'medium' ? '存在风险' :
                       riskAnalysis.risk === 'high' ? '风险较高' : '未评估'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{riskAnalysis.message}</p>
                </div>

                {/* 分布统计 */}
                <div className="space-y-3">
                  {['冲刺', '稳妥', '保底'].map(cat => {
                    const count = riskAnalysis.categories[cat as keyof typeof riskAnalysis.categories];
                    const percent = riskAnalysis.total > 0 ? Math.round((count / riskAnalysis.total) * 100) : 0;
                    const colors = categoryColors[cat];
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">{cat}</span>
                          <span className="font-medium text-gray-800">{count}个 ({percent}%)</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${colors.badge} rounded-full transition-all`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 建议 */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">填报建议</h4>
                  <ul className="text-xs text-gray-500 space-y-1.5">
                    <li>• 冲刺志愿建议占 20-30%</li>
                    <li>• 稳妥志愿建议占 40-50%</li>
                    <li>• 保底志愿建议占 25-35%</li>
                    <li>• 总数建议不少于20个</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 添加志愿弹窗 */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3 md:p-4" onClick={() => setShowAddDialog(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-lg text-gray-800">添加志愿</h3>
              <button onClick={() => setShowAddDialog(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 院校搜索 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isJunior ? '选择高中 *' : '选择院校 *'}
                </label>
                <input
                  type="text"
                  value={searchSchool}
                  onChange={e => { setSearchSchool(e.target.value); searchSchools(e.target.value); }}
                  placeholder={isJunior ? '搜索高中名称...' : '搜索院校名称...'}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {/* 高中生：大学搜索结果 */}
                {!isJunior && schoolResults.length > 0 && (
                  <div className="mt-2 bg-white border border-gray-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                    {schoolResults.map(school => (
                      <button
                        key={school.id}
                        onClick={() => { setSelectedSchool(school); setSearchSchool(school.name); setSchoolResults([]); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between"
                      >
                        <span className="font-medium text-gray-800">{school.name}</span>
                        <div className="flex gap-1">
                          {school.is985 === 1 && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">985</span>}
                          {school.is211 === 1 && <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">211</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {/* 初中生：高中搜索结果 */}
                {isJunior && highSchoolResults.length > 0 && (
                  <div className="mt-2 bg-white border border-gray-200 rounded-xl max-h-48 overflow-y-auto shadow-lg">
                    {highSchoolResults.map(hs => (
                      <button
                        key={hs.name}
                        onClick={() => { setSelectedHighSchool(hs); setSearchSchool(hs.name); setHighSchoolResults([]); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between"
                      >
                        <span className="font-medium text-gray-800">{hs.name}</span>
                        <div className="flex gap-1">
                          {hs.is_demonstration === 1 && <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">示范</span>}
                          {hs.school_type === '职高' && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">职高</span>}
                          {hs.school_type === '普高' && <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">普高</span>}
                          {hs.quota_type === '定向生' && <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded">定向</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {/* 已选学校 */}
                {selectedSchool && !isJunior && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-xl flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700">{selectedSchool.name}</span>
                    <button onClick={() => { setSelectedSchool(null); setSearchSchool(''); }} className="text-blue-400 hover:text-blue-600">
                      <X size={14} />
                    </button>
                  </div>
                )}
                {selectedHighSchool && isJunior && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-xl flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-700">{selectedHighSchool.name}</span>
                      <span className="text-xs text-blue-500 ml-2">{selectedHighSchool.district} · {selectedHighSchool.school_type} · {selectedHighSchool.score_line}分</span>
                    </div>
                    <button onClick={() => { setSelectedHighSchool(null); setSearchSchool(''); }} className="text-blue-400 hover:text-blue-600">
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* 专业（仅高考） */}
              {!isJunior && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">意向专业（选填）</label>
                  <input
                    type="text"
                    value={majorName}
                    onChange={e => setMajorName(e.target.value)}
                    placeholder="如：计算机类"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              )}

              {/* 志愿类型 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">志愿类型</label>
                <div className="flex gap-2">
                  {['冲刺', '稳妥', '保底'].map(cat => {
                    const colors = categoryColors[cat];
                    return (
                      <button
                        key={cat}
                        onClick={() => setCategory(cat)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-xl transition-colors ${
                          category === cat
                            ? `${colors.bg} ${colors.text} border-2 ${colors.border}`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAddDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleAdd}
                disabled={!selectedSchool || saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? '添加中...' : '添加'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
